import React, {
	type ChangeEvent,
	useEffect,
	useImperativeHandle,
	forwardRef,
	useMemo,
	useRef,
	useState,
	useCallback,
} from 'react'
import {
	AutocompleteControl,
	type GenericItem,
	SingleSelectMenu,
	useAutocomplete,
} from '@adsk/alloy-react-dropdown'
import locations from './data/locations'
import Theme from '@adsk/alloy-react-theme'
import PhoneUtil from '../../helpers/phoneUtil'
import _ from 'lodash'
import TextInput from '@adsk/alloy-react-text-input'
import { InfoPopover } from '@adsk/alloy-react-tooltip'
import { AlertWarningFilledIcon } from '@adsk/alloy-react-icon'
import styled from 'styled-components'
import { PhoneNumberFormat } from 'google-libphonenumber'
import {
	customMinDeviceSize,
	customMaxDeviceSize,
	device,
} from '@adsk/napi-shared'
import FormField from '@adsk/alloy-react-form-field'
import { defaultCountryFromRegion } from './utils/defaultCountryCodeUtil'
import HighZIndexInfoPopOver from './HighZIndexInfoPopOver'

const PHONE_REGEX =
	/^(?<number>\S+.*?)(?:\s+(?<ext_format>ext\.|x)\s*(?<extension>\S*))?$/
const EXTENSION_REGEX = /^\d*$/
interface PhoneTextInputDivProps {
	isError: boolean
}

const maxWidth768 = customMaxDeviceSize(768)
const minWidth769 = customMinDeviceSize(769)

export interface PhoneRef {
	isValidPhone: () => boolean
	isValidExtension: () => boolean
	internationalizePhoneNumber: (phone: string) => string
	formatPhoneWithExtension: () => string
}

interface PhoneExtensionInputProps {
	isVisible?: boolean
	labelValue: string
	labelPos: string
	errorMessage?: string
	onErrorInput?: () => void
	onValidInput?: () => void
}

interface PhoneInputProps {
	error?: boolean
	errorMessage?: string
	value: string
	updatePhone: React.Dispatch<React.SetStateAction<string>>
	ref?: React.Ref<PhoneRef>
	label?: {
		value?: string
		position?: string
	}
	// Region from which the component is accessed
	accessedRegion?: string
	extension?: PhoneExtensionInputProps
	onValidInput?: () => void
	onErrorInput?: () => void
}

interface CountryCodeProps {
	isoCountryCode: string
	countryName: string
	extCode: string
}

const CountryCodeIcon = styled.img`
	width: 20px;
	height: 20px;
	margin-right: 0;
	vertical-align: middle;
`
const ControlInputDiv = styled.div`
	min-width: 50%;
	padding-bottom: 8px;
	@media${device.mobileXL} {
		padding-bottom: 0;
	}
`

const CountryNameAndCode = styled.span`
	vertical-align: middle;
	margin-left: 5px;
`

const SelectedDivWrapper = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: inherit;
	pointer-events: none;
	user-select: none;
	flex: 1 1 0;
	width: 190px;
`

const DropDownDiv = styled.div`
	display: inline;
	text-overflow: ellipsis;
	flex-direction: row;
	justify-content: space-evenly;
`

const PhoneTextInputDiv = styled.div<PhoneTextInputDivProps>`
	display: flex;
	@media${maxWidth768} {
		width: 100%;
	}
`
const ExtensionTextInputDiv = styled.div`
	display: flex;
	flex: 1;
	@media${maxWidth768} {
		width: 100%;
	}
`
const StyledInfoPopover = styled(HighZIndexInfoPopOver)`
	display: flex;
	align-self: center;
	margin: auto 2%;
`

const CountryCodeElement = ({
	isoCountryCode,
	countryName,
	extCode,
}: CountryCodeProps): JSX.Element => {
	return (
		<DropDownDiv>
			<CountryCodeIcon
				data-testid={`countryCodeIcon-${isoCountryCode}`}
				src={`https://s3.amazonaws.com/static.buildingconnected.com/country-flags/${isoCountryCode.toLowerCase()}.svg`}
			/>
			<CountryNameAndCode>
				<b>{countryName}</b> ({extCode}){' '}
			</CountryNameAndCode>
		</DropDownDiv>
	)
}

const ResponsiveContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;

	@media${maxWidth768} {
		flex-direction: column;
	}

	@media${minWidth769} {
		flex-direction: row;
	}
`

const FieldContainer = styled.div`
	flex: 1;

	&:first-child {
		margin-right: 1%;
		@media${maxWidth768} {
			margin-bottom: 2%;
		}
	}

	&:nth-child(2) {
		label {
			display: none;
			color: transparent;
			@media${minWidth769} {
				display: block;
			}
		}
		@media${minWidth769} {
			margin-left: 1%;
		}
		@media${maxWidth768} {
			margin-bottom: 2%;
		}
	}

	/* Ensure the extension field container does not expand */
	&:nth-child(3) {
		flex: 0 0 20%;
		@media${minWidth769} {
			margin-left: 1%;
		}
	}

	@media${maxWidth768} {
		width: 100%;
	}
`

const GroupContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	justify-content: space-between;

	@media${minWidth769} {
		flex-direction: row;
		width: 100%;
	}
`

const PhoneInput: React.FC<PhoneInputProps> = forwardRef<
	PhoneRef,
	PhoneInputProps
>(
	(
		{
			accessedRegion = 'US',
			value: phone,
			updatePhone,
			error,
			errorMessage,
			...phoneProps
		},
		ref
	): JSX.Element => {
		const { colors } = Theme
		const {
			isVisible: isExtFieldVisible = true,
			errorMessage:
				extensionErrorMessage = 'Invalid extension. Only numbers are accepted.',
			labelValue: extLabelValue,
			labelPos: extLabelPos,
			onErrorInput: onExtensionErrorFn,
			onValidInput: onExtensionValidFn,
		} = phoneProps?.extension as PhoneExtensionInputProps
		const phoneObjectMatchGroup = phone?.trim().match(PHONE_REGEX)
		const [extension, setExtension] = useState<string>(
			phoneObjectMatchGroup?.groups?.extension ?? ''
		)
		const [extensionError, setExtensionError] = React.useState<boolean>(false)
		const extInputRef = useRef<HTMLInputElement>(null)
		const phoneInputRef = useRef<HTMLInputElement>(null)
		const currentPhoneCountryCode = PhoneUtil.getPhoneCountryCode(
			phone,
			defaultCountryFromRegion(accessedRegion)
		)
		const dropDownItems = useMemo(() => {
			return Object.keys(locations.countries).map(
				(isoCountryCode: string, index: number) => {
					const countryName = _.get(locations.countries, isoCountryCode).name
					const countryExtCode = _.get(locations.countries, isoCountryCode).code
					return {
						value: isoCountryCode,
						label: (
							<CountryCodeElement
								isoCountryCode={isoCountryCode}
								countryName={countryName}
								extCode={countryExtCode}
							/>
						),
						highlightedIndex: index,
						countryName,
					}
				}
			) as GenericItem[]
		}, [])
		const [selectedOption, setSelectedOption] = useState<GenericItem>(
			dropDownItems.find(
				(item: GenericItem) =>
					(item.value as string) === currentPhoneCountryCode
			) as GenericItem
		)
		const updatePhoneFlag = (): void => {
			const currentPhoneCountryCode = PhoneUtil.getPhoneCountryCode(phone)
			setSelectedOption(
				dropDownItems.find(
					(item: GenericItem) => item.value === currentPhoneCountryCode
				) as GenericItem
			)
		}

		const isValidExtension = (): boolean => {
			const extensionValue =
				extInputRef?.current?.value?.trim() as unknown as string
			return EXTENSION_REGEX.test(extensionValue)
		}

		const isValidPhone = (): boolean => {
			const phoneNumberInput = phoneInputRef.current
			if (phoneNumberInput != null) {
				if (phoneNumberInput?.value === '') {
					// empty phone is allowed
					return true
				} else {
					return PhoneUtil.isValidPhoneNumberForRegion(
						phoneNumberInput?.value,
						selectedOption.value as string
					)
				}
			}
			return false
		}

		const updatePhoneFn = (): void => {
			if (isValidExtension()) {
				setExtensionError(false)
				onExtensionValidFn?.()
			} else {
				setExtensionError(true)
				onExtensionErrorFn?.()
			}
			if (isValidPhone()) {
				if (phoneInputRef?.current?.value?.trim() === '') setExtension('')
				phoneProps?.onValidInput?.()
			} else {
				phoneProps?.onErrorInput?.()
			}
		}

		useEffect(() => {
			if (PhoneUtil.isValidPhoneNumberForRegion(phone)) {
				updatePhoneFlag()
			}
		}, [phone])

		useEffect(() => {
			updatePhoneFn()
		}, [phone, extension])

		useImperativeHandle(ref, () => {
			const internationalizePhoneNumber = (phone: string): string => {
				return PhoneUtil.formatPhoneNumber(
					phone,
					selectedOption.value as string,
					PhoneNumberFormat.INTERNATIONAL
				) as string
			}

			return {
				isValidPhone,
				isValidExtension,
				formatPhoneWithExtension: (): string => {
					const internationalizeNumber: string = internationalizePhoneNumber(
						phoneInputRef?.current?.value ?? ''
					)
					const extension =
						internationalizeNumber?.trim() === ''
							? ''
							: (extInputRef?.current?.value?.trim() as unknown as string)
					return extension?.trim() !== ''
						? `${internationalizeNumber} ext. ${extension?.trim()}`
						: internationalizeNumber
				},
				internationalizePhoneNumber,
			}
		})

		const onSelectItemChangeHandler = ({
			selectedItem,
		}: {
			selectedItem: GenericItem | null | undefined
		}): void => {
			if (selectedItem != null) {
				const regionCode = _.get(selectedItem, 'value') as string
				if (phone !== '') {
					const formattedPhone = PhoneUtil.formatPhoneNumber(
						phone,
						regionCode
					) as string
					updatePhone(formattedPhone)
				} else {
					updatePhone('')
				}
				setSelectedOption(selectedItem)
			}
		}

		const filterItems = useCallback(
			({
				inputValue,
				allItems,
			}: {
				inputValue: string
				allItems: GenericItem[]
			}) => {
				return allItems.filter((item) => {
					const countryName = _.get(item, 'countryName') as string
					return countryName.toLowerCase().includes(inputValue.toLowerCase())
				})
			},
			[]
		)

		const { controlProps, menuProps } = useAutocomplete({
			items: dropDownItems,
			onSelectedItemChange: ({ selectedItem }): void => {
				onSelectItemChangeHandler({ selectedItem })
			},
			selectedItem: selectedOption,
			getFilteredItems: filterItems,
		})

		return (
			<ResponsiveContainer>
				<GroupContainer>
					<FieldContainer>
						<FormField
							label={phoneProps?.label?.value}
							labelVariant={phoneProps?.label?.position}
						>
							<ControlInputDiv>
								<AutocompleteControl
									{...controlProps}
									data-testid={'phoneFlagTestId'}
									style={{
										background: colors.white,
										overflow: 'auto',
									}}
									renderItem={({ selectedItem, isOpen }) => {
										return isOpen ? null : (
											<SelectedDivWrapper>
												{selectedItem?.label as React.ReactNode}
											</SelectedDivWrapper>
										)
									}}
								/>
								<SingleSelectMenu
									{...menuProps}
									style={{
										'white-space': 'none',
									}}
								/>
							</ControlInputDiv>
						</FormField>
					</FieldContainer>
					<FieldContainer>
						<FormField
							// NOTE: This will be hidden by CSS  - added for consistent padding
							label={'Phone Number'}
							labelVariant={phoneProps?.label?.position}
						>
							<PhoneTextInputDiv isError={Boolean(error)}>
								<TextInput
									type={TextInput.TYPES.TEXT}
									value={PhoneUtil.formatPhoneNumber(
										phoneObjectMatchGroup?.groups?.number ?? '',
										selectedOption.value as string
									)}
									error={error ?? false}
									ref={phoneInputRef}
									data-testid={'phoneInputTestId'}
									placeholder={'Contact number'}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										const regionCode = _.get(selectedOption, 'value') as string
										let newValue = e.target.value
										if (newValue !== undefined) {
											newValue = PhoneUtil.formatPhoneNumber(
												newValue,
												regionCode
											) as string
										}
										updatePhone(newValue)
									}}
								/>
								{error === true && (
									<StyledInfoPopover
										message={
											errorMessage ??
											'Phone number format is not supported by the country selected.'
										}
										renderIcon={() => (
											<AlertWarningFilledIcon color={'#EC4A41'} size={15} />
										)}
										placement={InfoPopover.PLACEMENTS.TOP}
										variant={InfoPopover.VARIANTS.DARK}
									/>
								)}
							</PhoneTextInputDiv>
						</FormField>
					</FieldContainer>
					{isExtFieldVisible && (
						<FieldContainer>
							<FormField
								data-testid={'extensionLabelTestId'}
								label={extLabelValue}
								labelVariant={extLabelPos}
							>
								<ExtensionTextInputDiv>
									<TextInput
										ref={extInputRef}
										data-testid={'extensionInputTestId'}
										type={TextInput.TYPES.TEXT}
										error={extensionError}
										maxLength={6}
										placeholder={'Ext.'}
										value={extension}
										disabled={
											!isValidPhone() ||
											phoneInputRef?.current?.value?.trim() === ''
										}
										onChange={(e) => {
											setExtension(e.target.value)
										}}
									/>
									{extensionError && (
										<StyledInfoPopover
											message={extensionErrorMessage}
											renderIcon={() => (
												<AlertWarningFilledIcon color={'#EC4A41'} size={15} />
											)}
											placement={InfoPopover.PLACEMENTS.TOP}
											variant={InfoPopover.VARIANTS.DARK}
										/>
									)}
								</ExtensionTextInputDiv>
							</FormField>
						</FieldContainer>
					)}
				</GroupContainer>
			</ResponsiveContainer>
		)
	}
)

PhoneInput.displayName = 'PhoneInput'
export default PhoneInput
